import {formatKpiLabel, formatKpiValue, KpiData} from '@domain/kpis'
import {KpiScore} from '@hconnect/uikit/src/lib2'
import {Box, Grid, Theme, useMediaQuery} from '@mui/material'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {KPIS_WIDGET_NAMESPACE, NO_VALUE_STRING} from '../consts'

import {KpiThresholdIcon} from './KpiThresholdIcon'

type Props = {
  kpiItems: KpiData[]
  gridColumns?: number
  gridItemXs?: number
  kpiSize?: 'XL' | 'L' | 'M' | 'S' | 'XS'
  showKpiValueLoader?: boolean
  isMobile?: boolean
}

const SINGLE_COLUMN_BREAKPOINT = 380

export const PlantStatusDailyKpis: React.FC<Props> = ({
  kpiItems,
  gridColumns = 12,
  gridItemXs = 6,
  kpiSize = 'L',
  showKpiValueLoader,
  isMobile
}: Props) => {
  const {t, i18n} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const language = i18n.language

  const kpiItemsFormatted = useMemo(
    () =>
      kpiItems?.map((kpi) => ({
        ...kpi,
        label: formatKpiLabel(kpi, t),
        value: formatKpiValue(kpi.value, language)
      })) ?? [],
    [kpiItems, language, t]
  )

  const isSingleColumn = useMediaQuery(({breakpoints}: Theme) =>
    breakpoints.down(SINGLE_COLUMN_BREAKPOINT)
  )

  const hasOverlengthLabels = kpiItemsFormatted.some((item) => (item.label + '').length > 72)

  return (
    <Grid container columns={gridColumns} spacing={isMobile ? 2 : 5} sx={{maxWidth: '100%'}}>
      {kpiItemsFormatted.map((kpiItem) => (
        <Grid item xs={isSingleColumn ? 12 : gridItemXs} key={kpiItem.key} sx={{display: 'flex'}}>
          <Box sx={{flexGrow: 1, height: '100%'}}>
            <KpiScore
              value={kpiItem.value ?? NO_VALUE_STRING}
              variant={kpiItem.value ? 'neutral' : 'no-value'}
              label={kpiItem.label ?? NO_VALUE_STRING}
              unit={kpiItem.value ? kpiItem.unit : undefined}
              valueIcon={
                kpiItem.kpiBreachSeverity ? (
                  <KpiThresholdIcon status={kpiItem.kpiBreachSeverity} variant="filled" />
                ) : undefined
              }
              size={kpiSize}
              showValueLoader={showKpiValueLoader}
              data-test-id={`kpi-score-${kpiItem.key}`}
              transparentIconLabelSxProp={{minHeight: isMobile && hasOverlengthLabels ? 64 : 48}}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}
